import axios from "axios";
import { t } from "i18next";
import { z } from "zod";

import languageCodeList from "../../constants/languages";

import type { AxiosError } from "axios";

import type { LanguageCode } from "../../api/ProjectAPI.types";
import type {
  CreateTranslationResponse,
  SmartlistTranslation,
  SmartlistTranslationToCreate,
} from "../../api/SmartlistAPI.types";
import type { Picture, TranslationsIndexes } from "../../types";

export function getSmartlistTranslationSchema() {
  return z.object({
    language: z.enum(languageCodeList),
    language_uuid: z.string().optional(),
    default: z.boolean(),
    uuid: z.string().optional(),
    name: z
      .string()
      .min(1, t("form:required", { field: t("form:fields.title") })),
    slug: z.string().optional(),
    short_description: z
      .string()
      .nullable()
      .optional()
      .transform((v) => v ?? ""),
    description: z
      .string()
      .nullable()
      .optional()
      .transform((v) => v ?? ""),
    picture: z.custom<Picture>().optional(),
  });
}

export function getSmartlistTranslationsFormSchema() {
  return z.object({
    translations: z.array(getSmartlistTranslationSchema()),
  });
}

export interface SmartlistTranslationFormField {
  language: LanguageCode;
  language_uuid?: string;
  default: boolean;
  uuid?: string;
  name: string;
  slug?: string;
  short_description?: string | null;
  description?: string | null;
  picture?: Picture;
}

/**
 * Converts API translation objects to the form field format used by React Hook Form.
 */
export function apiToFormTranslations(
  translations: SmartlistTranslation[],
): SmartlistTranslationFormField[] {
  return [...translations]
    .sort((a, b) => {
      if (a.default) return -1;
      if (b.default) return 1;
      return a.language.localeCompare(b.language);
    })
    .map((translation) => ({
      language: translation.language as LanguageCode,
      language_uuid: translation.language_uuid,
      default: translation.default,
      uuid: translation.uuid,
      name: translation.name ?? "",
      slug: translation.slug ?? "",
      short_description: translation.short_description ?? "",
      description: translation.description ?? "",
      picture: translation.picture ?? undefined,
    }));
}

/**
 * Adapts a CreateTranslationResponse (API response from creating a new translation)
 * into the SmartlistTranslationFormField shape expected by the form.
 * Normalizes null values to empty strings so RHF defaultValues stay consistent
 * with how field components (e.g. MarkdownEditorField) initialize.
 */
export function createTranslationResponseToFormField(
  response: CreateTranslationResponse,
  language: LanguageCode,
): SmartlistTranslationFormField {
  return {
    uuid: response.uuid,
    default: response.default,
    language_uuid: response.language_uuid,
    name: response.name ?? "",
    slug: response.slug ?? "",
    short_description: response.short_description ?? "",
    description: response.description ?? "",
    language,
  };
}

interface SmartlistApiError {
  message: string;
  reason: string;
}

/**
 * Extracts API error entries from an axios error response and returns
 * the corresponding translated strings. Returns an empty array when the
 * error body does not contain a structured error list.
 */
export function getSmartlistApiErrorMessageList(error: unknown): string[] {
  if (!axios.isAxiosError(error)) return [];

  const apiErrors = (error as AxiosError<{ error: SmartlistApiError[] }>)
    .response?.data?.error;

  if (!apiErrors?.length) return [];

  return apiErrors.map(({ message, reason }) => {
    const translationKey = reason
      ? `error:${reason}.${message}`
      : `error:${message}`;
    return t(translationKey);
  });
}

/**
 * Converts form field values back to the API shape.
 * Computes translations_indexes dynamically — no need to maintain it during editing.
 */
export function formToApiTranslations(
  formTranslations: SmartlistTranslationFormField[],
): {
  translations: SmartlistTranslationToCreate[];
  translations_indexes: TranslationsIndexes;
} {
  const translations: SmartlistTranslationToCreate[] = formTranslations.map(
    (field) => ({
      uuid: field.uuid,
      default: field.default,
      language: field.language,
      language_uuid: field.language_uuid ?? "",
      name: field.name ?? "",
      slug: field.slug ?? "",
      short_description: field.short_description ?? "",
      description: field.description ?? "",
      picture: field.picture,
    }),
  );

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const language_uuid: Record<string, number[]> = {};
  let defaultIndex = 0;

  formTranslations.forEach((field, index) => {
    if (field.language_uuid) {
      language_uuid[field.language_uuid] = [index];
    }
    if (field.default) {
      defaultIndex = index;
    }
  });

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const translations_indexes: TranslationsIndexes = {
    language_uuid,
    default: { true: [defaultIndex] },
  };

  return { translations, translations_indexes };
}
